import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import {
  of,
  tap,
  delay,
  switchMap,
  takeUntil,
  auditTime,
  animationFrameScheduler,
  debounceTime,
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
} from "rxjs";
import colors from "../../../../../../../tailwind.colors";
import createAdjustableCanvas from "./createAdjustableCanvas";
import TranscriptLabelInfo from "./TranscriptLabelInfo";

function controller({ props$, destroyed$ }) {
  const columnWidthPx = 180;
  const canvas = createAdjustableCanvas({ fontSize: 10 });
  const labels$ = new BehaviorSubject([]);
  let grid = {};
  let verticalLabelsContainer = { style: {} };
  let pxPos = -500; // random magic numer - out of view

  function raycastRegion(stage, meshes, px1, px2, pxStep, py1, py2, pyStep) {
    for (let x = px1; x <= px2; x += pxStep) {
      for (let y = py1; y <= py2; y += pyStep) {
        const { item } = stage.mainScene.raycast(meshes, x, y) || {};
        if (item) return item;
      }
    }
    return null;
  }

  function calcColumnOffset() {
    const { scale: xs } = props$.value.track.zoomService.xVisScale$.value;
    const domain1 = xs.invert(0);
    const colWidthInBases = Math.abs(domain1 - xs.invert(columnWidthPx));
    const offsetInBases = domain1 % colWidthInBases;
    const offsetInPx = xs(domain1 + offsetInBases);
    return offsetInPx;
  }

  function reallocatePrev(xScale, offset, width) {
    const els = Object.values(grid).filter(Boolean);
    const newGrid = {};
    for (let i = 0; i < els.length; i++) {
      const { transcript } = els[i];
      const x0 = xScale.scale(transcript.region.g0);
      const x1 = xScale.scale(transcript.region.g1);
      if (x1 < 0 || x0 > width) continue;
      const x = x0 + offset;
      const col = Math.floor(x / columnWidthPx);
      const { level } = transcript;
      const key = `${level}-${col}`;
      newGrid[key] = transcript;
    }

    grid = newGrid;
    return null;
  }

  function calcGrid() {
    const { elWidth: width } = props$.value.track?.stage || {};
    const { track, overlayData } = props$.value;
    const { meshes: m, stage: s, pTop, absoluteOffsetY, contentHeight } = track;
    const { zoomService } = track;
    const { y: scrollTop } = zoomService.zoomId$.value;
    const xScale = zoomService.xVisScale$.value;

    const { floorHeight } = overlayData;
    const floors = Math.floor(contentHeight / floorHeight);
    const columns = Math.ceil(width / columnWidthPx) + 1;
    const columnOffset = calcColumnOffset();
    const pxStep = 20;
    const pyStep = 20;
    const addedItemIds = new Set();
    const topDist = pTop + absoluteOffsetY + scrollTop;

    reallocatePrev(xScale, columnOffset, width);

    for (let f = 0; f < floors; f++) {
      for (let c = 0; c < columns; c++) {
        const key = `${f}-${c}`;

        if (grid[key]) continue;

        const py1 = floorHeight * (f + 0.5) + topDist;
        const py2 = py1;
        const px1 = c * columnWidthPx - columnOffset;
        const px2 = px1 + columnWidthPx;
        const item = raycastRegion(s, m, px1, px2, pxStep, py1, py2, pyStep);
        const itemId = item?.transcript?.id;
        const gridItem = addedItemIds.has(itemId) ? null : item;
        grid[key] = gridItem;
        addedItemIds.add(itemId);
      }
    }
  }

  function renderGrid() {
    canvas.clearCanvas();
    const { floorHeight, maxLevel } = props$.value.overlayData;
    const { track } = props$.value;
    const { xVisScale$ } = track.zoomService;
    const { scale: xScale } = xVisScale$.value;
    const els = Object.values(grid).filter(Boolean);
    const { isDark } = track.getPreferences();

    const px = 3;
    const py = 2;
    const bg = isDark ? colors.blueGray["700"] : colors.blueGray["200"];
    const fg = isDark ? colors.blueGray["200"] : colors.blueGray["700"];
    const r = 3;

    for (let i = 0; i < els.length; i++) {
      const { level, region, geneSymbol } = els[i].transcript;
      const x0 = xScale(region.g0);
      const x1 = xScale(region.g1);
      const x = Math.min(x1, Math.max(10, x0 - 2));
      const txt = geneSymbol;
      if (level > maxLevel - 1) continue;
      const y = (level + 0.5) * floorHeight + track.pTop - 20;
      canvas.drawTextRect({ txt, x, y, px, py, bg, fg, r });
    }
  }

  function adjustCanvas() {
    const { elWidth: width } = props$.value.track?.stage || {};
    const { pTop, contentHeight, pBottom } = props$.value.track;
    const height = pTop + contentHeight + pBottom;
    canvas.setDimensions(width, height);
  }

  function setVerticalLabelsContainer(el) {
    verticalLabelsContainer = el;
  }

  function repositionVertical(px) {
    verticalLabelsContainer.style.transform = `translateX(${px}px)`;
  }

  function recalcVerticalLabels(forceCalc) {
    if (
      !forceCalc &&
      !props$.value.track.stage?.mainScene?.isRaycastValid$.value
    ) {
      return;
    }
    const { track, overlayData } = props$.value;
    const { meshes: m, pTop, absoluteOffsetY, contentHeight } = track;
    const { zoomService } = track;
    const { y: scrollTop } = zoomService.zoomId$.value;
    const { floorHeight } = overlayData;
    const floors = Math.ceil(contentHeight / floorHeight);
    const topDist = pTop + absoluteOffsetY + scrollTop;

    const items = new Array(floors)
      .fill(0)
      .map((_, i) => floorHeight * (i + 0.5) + topDist)
      .map((py) => track.stage.mainScene.raycast(m, pxPos, py, 2)?.item)
      .filter(Boolean)
      .map((x) => {
        const { transcript } = x;
        return {
          ...transcript,
          y: (transcript.level + 0.5) * floorHeight + track.pTop - 27,
          exonIndex: x.exonIndex || x.exon?.exonIndex,
          exonTypeIndex: x.typeIndex,
          codonIndex: x.codonIndex,
          aminoAcid: x.aminoAcid,
        };
      });

    labels$.next(items);
  }

  props$
    .pipe(
      delay(200),
      switchMap((x) => x.track.zoomService.xVisScale$),
      debounceTime(500),
      tap(adjustCanvas),
      tap(calcGrid),
      tap(renderGrid),
      tap(() => recalcVerticalLabels(true)),
      catchError((e) => of(e)),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.track.zoomService.xVisScale$),
      auditTime(0, animationFrameScheduler),
      tap(renderGrid),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.track.zoomService.mousePosX$),
      auditTime(0, animationFrameScheduler),
      distinctUntilChanged((a, b) => a === b),
      tap(repositionVertical),
      auditTime(500),
      tap((x) => (pxPos = x)),
      tap(() => recalcVerticalLabels(false)),
      takeUntil(destroyed$)
    )
    .subscribe();

  return {
    canvas: canvas.getCanvasEl(),
    setVerticalLabelsContainer,
    labels: labels$,
  };
}

function GeneOverlay({
  canvas,
  setVerticalLabelsContainer,
  labels,
  overlayData,
}) {
  const canvasContainerRef = useRef(null);
  const verticalLabelsRef = useRef(null);
  const { maxLevel, currentMaxLevel: curMax, setRowLimit } = overlayData;
  const hiddenRows = curMax - maxLevel;
  const maxLevelSuggestion = hiddenRows ? curMax : 1;

  useEffect(() => {
    const { current: canvasEl } = canvasContainerRef;
    const { current: verticalLabelsEl } = verticalLabelsRef;

    if (canvasEl && verticalLabelsEl) {
      canvasEl.appendChild(canvas);
      setVerticalLabelsContainer(verticalLabelsEl);
    }
  }, [setVerticalLabelsContainer]);

  return (
    <>
      <div
        ref={canvasContainerRef}
        className="tw-absolute tw-inset-0 tw-pointer-events-none tw-overflow-hidden tw-opacity-80"
      />

      {hiddenRows > 0 ? (
        <div className="tw-h-6 tw-absolute tw-bottom-0 tw-left-0 tw-right-0 bg-grad-vert-1" />
      ) : null}

      <div
        ref={verticalLabelsRef}
        className="tw-absolute tw-inset-0 color-1 tw-pointer-events-none"
      >
        {labels.map((x) =>
          x.level === maxLevel ? null : (
            <div key={x.transcriptName}>
              <div
                className="tw-top-0 tw-left-0 tw-absolute"
                style={{ transform: `translate(-100%, ${x.y}px)` }}
              >
                <div className="tw-mx-px tw-pr-1 bg-1 tw-pl-2 text-3xs">
                  <div className=" tw-bg-indigo-600 tw-text-white tw-rounded-sm tw-font-bold tw-px-0.5">
                    {x.geneSymbol}
                  </div>
                </div>
              </div>
              <div
                style={{ transform: `translateY(${x.y}px)` }}
                className="tw-absolute tw-top-0 tw-left-0 text-3xs"
              >
                <div className="tw-mx-px tw-px-1 bg-1">
                  <TranscriptLabelInfo item={x} />
                </div>
              </div>
            </div>
          )
        )}

        <div className="tw-absolute tw-bottom-0.5 tw-left-0 tw-right-0 text-1 tw-opacity-60 hover:tw-opacity-100">
          {hiddenRows > 0 ? (
            <div
              data-tooltip={`
                <div class="tw-p-2">
                <b>Click to show ${hiddenRows} hidden rows.</b> <br/> 
                You can manually update the maximum row limit in the track's filter settings.
                </div>
              `}
              data-tooltip-dangerous-html={1}
              data-tooltip-class="genome-browser-small-tooltip"
              style={{ transform: "translateX(-50%)" }}
              onClick={() => setRowLimit(maxLevelSuggestion)}
              className="tw-inline-block shadow-2xl tw-bg-orange-600 tw-px-1 tw-rounded-sm tw-text-white tw-font-bold tw-cursor-pointer tw-pointer-events-auto"
            >
              Show more rows <i className="fa fa-exclamation-circle tw-ml-1" />
            </div>
          ) : null}

          {hiddenRows <= 0 && maxLevel !== 1 ? (
            <div
              data-tooltip="You can manually update the maximum row limit in the track's filter settings"
              data-tooltip-class="genome-browser-small-tooltip"
              style={{ transform: "translateX(-50%)" }}
              onClick={() => setRowLimit(maxLevelSuggestion)}
              className="tw-inline-block shadow-2xl tw-bg-blueGray-600 tw-px-1 tw-rounded-sm tw-text-white tw-font-bold tw-cursor-pointer tw-pointer-events-auto"
            >
              Show fewer rows
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

GeneOverlay.propTypes = {
  overlayData: PropTypes.shape({
    maxLevel: PropTypes.number.isRequired,
    currentMaxLevel: PropTypes.number.isRequired,
    floorHeight: PropTypes.number.isRequired,
    setRowLimit: PropTypes.func.isRequired,
  }).isRequired,
  labels: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  canvas: PropTypes.instanceOf(HTMLCanvasElement).isRequired,
  setVerticalLabelsContainer: PropTypes.func.isRequired,
};

export default xstream(controller)(GeneOverlay);
